import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { Shield, LogOut, Bell, ClipboardList, Users, Calendar, Check } from "lucide-react";

const NOTIF_LABEL = {
  "New Request": "New request",
  "Urgent Request": "Urgent request",
  "New Message": "New customer message",
  "Visit Request": "Visit requested",
  "Appointment Accepted": "Customer accepted time",
  "Appointment Change Requested": "Customer requested another time",
  "Time Proposed": "Time proposed",
  "Time Changed": "Time changed",
  "Appointment Cancelled": "Appointment cancelled",
};

export function AdminNav({ active }) {
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/notifications");
      setNotifs(data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("mrshu_admin_token");
        nav("/admin/login");
      }
    }
  }, [nav]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const openNotif = async (n) => {
    await adminApi.post(`/admin/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    nav(`/admin/jobs/${n.job_id}`);
  };

  const link = (to, label, icon, key) => (
    <button data-testid={`adminnav-${key}`} onClick={() => nav(to)}
      className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors ${active === key ? "bg-white text-primary" : "bg-white/10 hover:bg-white/20"}`}>
      {icon} <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-2">
        <button onClick={() => nav("/admin")} className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div className="text-left hidden md:block">
            <h1 className="font-display text-lg font-bold leading-none">MR SHU Admin</h1>
            <p className="text-xs text-primary-foreground/70">Christchurch</p>
          </div>
        </button>

        <nav className="flex items-center gap-2">
          {link("/admin", "Requests", <ClipboardList className="h-4 w-4" />, "requests")}
          {link("/admin/customers", "Customers", <Users className="h-4 w-4" />, "customers")}
          {link("/admin/calendar", "Calendar", <Calendar className="h-4 w-4" />, "calendar")}

          <div className="relative" ref={ref}>
            <button data-testid="adminnav-notifications-btn" onClick={() => setOpen((o) => !o)}
              className="relative flex items-center rounded-full bg-white/10 p-2.5 hover:bg-white/20 transition-colors">
              <Bell className="h-4 w-4" />
              {unread > 0 && (
                <span data-testid="notif-unread-count" className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold">
                  {unread}
                </span>
              )}
            </button>
            {open && (
              <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto rounded-2xl border border-border bg-white text-foreground shadow-xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <span className="font-semibold text-primary">Notifications</span>
                  <button data-testid="notif-read-all" onClick={async () => { await adminApi.post("/admin/notifications/read-all"); load(); }}
                    className="text-xs text-primary flex items-center gap-1"><Check className="h-3 w-3" /> Mark all read</button>
                </div>
                {notifs.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-muted-foreground text-center">No notifications.</p>
                ) : notifs.slice(0, 40).map((n) => (
                  <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openNotif(n)}
                    className={`block w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary transition-colors ${n.read ? "" : "bg-accent/40"}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${n.type === "Urgent Request" ? "text-destructive" : "text-primary"}`}>{NOTIF_LABEL[n.type] || n.type}</span>
                      <span className="text-xs text-muted-foreground">{n.ref_number}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{n.customer_name} · {new Date(n.created_at).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button data-testid="admin-logout-btn" onClick={() => { localStorage.removeItem("mrshu_admin_token"); nav("/"); }}
            className="flex items-center rounded-full bg-white/10 p-2.5 hover:bg-destructive transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </nav>
      </div>
    </header>
  );
}
